/**
 * Auth composable
 *
 * Provides authentication logic and user session management
 */

import { computed } from 'vue'
import {canKeepMasqueradeLocation} from './masqueradeLocation'
import { useRouter } from 'vue-router'
import { useAuthStore, type AuthUser } from '@/stores'
import {
  authAPI,
  type LoginResponse,
  type LoginOtpRequest,
  type LoginOtpVerifyRequest,
  type RegisterOtpResendRequest,
  type RegisterOtpVerifyRequest,
  type RegisterRequest,
  type UserResponse,
} from '@/api'

export const normalizeAuthUser = (response: LoginResponse | UserResponse): AuthUser => {
  const source: any = 'user' in response ? response.user : response

  if (!source.id || !source.email) {
    throw new Error('Login response did not include user')
  }

  const firstName = source.firstName || ''
  const lastName = source.lastName || ''
  const fallbackName = `${firstName} ${lastName}`.trim()

  return {
    id: source.id,
    email: source.email,
    name: source.name || fallbackName || source.email,
    role: source.role,
    details: source,
  }
}

export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()

  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const user = computed(() => authStore.user)

  const acceptSession = (response: LoginResponse) => {
    authStore.setSession(response.token, normalizeAuthUser(response))
  }

  const login = async (email: string, password: string) => {
    const response = await authAPI.login({ email, password })
    authStore.setSession(response.token, normalizeAuthUser(response))
  }

  const requestLoginOtp = async (request: LoginOtpRequest) => {
    return authAPI.requestLoginOtp(request)
  }

  const verifyLoginOtp = async (request: LoginOtpVerifyRequest) => {
    const response = await authAPI.verifyLoginOtp(request)
    authStore.setSession(response.token, normalizeAuthUser(response))
    return response
  }

  const register = async (request: RegisterRequest) => {
    return authAPI.register(request)
  }

  const verifyRegistrationOtp = async (request: RegisterOtpVerifyRequest) => {
    const response = await authAPI.verifyRegistrationOtp(request)
    authStore.setSession(response.token, normalizeAuthUser(response))
    return response
  }

  const resendRegistrationOtp = async (request: RegisterOtpResendRequest) => {
    return authAPI.resendRegistrationOtp(request)
  }

  const searchMasqueradeTargets = async (query: string, limit = 8) => {
    const response = await authAPI.searchMasqueradeTargets(query, limit)
    return response.users
  }

  const startMasquerade = async (
    password: string,
    targetUserId: string,
    targetEmail: string
  ) => {
    const location = router.currentRoute.value
    const response = await authAPI.startMasquerade({
      password,
      targetUserId,
      targetEmail,
    })
    const target = normalizeAuthUser(response)
    authStore.beginMasquerade(response.token, target)
    const keep = await canKeepMasqueradeLocation(location, target)
    if (authStore.token === response.token && router.currentRoute.value.fullPath === location.fullPath && !keep) await router.replace('/')
  }

  const stopMasquerade = async () => {
    authStore.stopMasquerade()
  }

  const requestPasswordReset = async (email: string) => {
    await authAPI.requestPasswordReset({ email })
  }

  const verifyPasswordToken = async (token: string) => {
    return authAPI.verifyPasswordToken(token)
  }

  const requestNewPasswordToken = async (token: string) => {
    await authAPI.requestNewPasswordToken(token)
  }

  const resetPassword = async (
    token: string,
    password: string,
    confirmPassword: string
  ) => {
    return authAPI.resetPassword(token, { password, confirmPassword })
  }

  const logout = async () => {
    const departingToken = authStore.token
    // Lock all local account content immediately, including without a network.
    authStore.logout()
    router.push('/login')
    if (departingToken) {
      try { await authAPI.logout(undefined, departingToken) } catch { /* Local logout is already complete. */ }
    }
  }

  const getCurrentUser = async () => {
    if (!authStore.token) {
      return null
    }
    try {
      const userResponse = await authAPI.getCurrentUser()
      authStore.setUser(normalizeAuthUser(userResponse))
      return userResponse
    } catch {
      authStore.logout()
      return null
    }
  }

  return {
    isAuthenticated,
    user,
    acceptSession,
    login,
    requestLoginOtp,
    verifyLoginOtp,
    register,
    verifyRegistrationOtp,
    resendRegistrationOtp,
    searchMasqueradeTargets,
    startMasquerade,
    stopMasquerade,
    requestPasswordReset,
    verifyPasswordToken,
    requestNewPasswordToken,
    resetPassword,
    logout,
    getCurrentUser,
  }
}
